// ============================================================================
// REPORT THEME UTILITIES — shared helpers for theming AI consultant content.
//
// The AI content renderers (Process Health, AI Opportunities, Quick
// Healthcheck, Framework Assessment, Execution Strategy, Trend Analysis)
// were written against the classic Atlassian palette, so their raw output IS
// the Atlassian theme. themeAiHtml() retints that output to the ServiceNow
// "Petrol & Green" theme via a colour-for-colour remap, which keeps the six
// renderers untouched.
// ============================================================================

import { DEFAULT_REPORT_THEME } from '@process-advisor/process-miner/consultant/reportThemes';
import { getAppVersion } from '@process-advisor/process-miner/consultant/workbenchShared';

// On-screen and default export theme follows the app edition:
// ServiceNow build ('SNOW') -> servicenow theme, Forge/Jira build -> atlassian.
export const appDefaultReportTheme = () =>
  (getAppVersion() === 'SNOW' ? 'servicenow' : getAppVersion() ? 'atlassian' : DEFAULT_REPORT_THEME);

// Classic-Atlassian -> ServiceNow "Petrol & Green" colour map. Keys uppercase.
const ADS_TO_SERVICENOW = {
  // Blues -> teals
  '#0052CC': '#0E7C8C',
  '#0C66E4': '#0FA0A6',
  '#0747A6': '#0A4A57',
  '#1D7AFC': '#0FA0A6',
  '#4C8FEB': '#3FA8AE',
  '#85B7EB': '#7FC4C8',
  '#B3D4FF': '#A9D8D8',
  '#CCE0FF': '#BFE3E2',
  '#DEEBFF': '#DCEEED',
  '#E9F2FF': '#E9F5F4',
  '#E6F1FB': '#E9F5F4',
  '#E8EEFF': '#E9F5F4',
  '#F0F7FF': '#F0F8F7',
  '#F8F9FF': '#F5FBFA',
  '#091E42': '#0C2F3F',
  '#172B4D': '#0C2F3F',
  '#1E2432': '#122A35',
  // Cyans -> teal family
  '#00B8D9': '#0FA0A6',
  '#00A3BF': '#12949B',
  '#006C80': '#0A5A61',
  '#79E2F2': '#8AD8DC',
  '#E6FCFF': '#E4F5F6',
  // Neutral text
  '#42526E': '#41525F',
  '#505F79': '#4E5E66',
  '#5E6C84': '#5C6E7A',
  '#6B778C': '#6A7A86',
  '#8892B0': '#88969E',
  '#97A0AF': '#93A1A8',
  '#C1C7D0': '#BFC9CB',
  // Surfaces & borders
  '#F4F5F7': '#F3F6F6',
  '#F7F8F9': '#F5F9F9',
  '#F7F8FA': '#F6FAF9',
  '#FAFBFC': '#FAFBFB',
  '#EBECF0': '#E7EBEB',
  '#DFE1E6': '#DAE2E2',
  '#E8E9EB': '#E5E9EB',
  // Danger
  '#DE350B': '#D92D20',
  '#BF2600': '#B42318',
  '#FF8F73': '#FDA29B',
  '#FFBDAD': '#FECDCA',
  '#FFEBE6': '#FEE4E2',
  '#FFF8F6': '#FEF3F2',
  // Warn
  '#FF8B00': '#F79009',
  '#974F0C': '#B54708',
  '#FFE380': '#FEDF89',
  '#FFF0B3': '#FEF0C7',
  '#FFF7D6': '#FFFAEB',
  '#FFFBEE': '#FFFCF5',
  '#FFFCF0': '#FFFAEB',
  // Success
  '#00875A': '#067647',
  '#006644': '#05603A',
  '#1F845A': '#079455',
  '#216E4E': '#067647',
  '#57D9A3': '#47CD89',
  '#7EE2B8': '#75E0A7',
  '#79F2C0': '#86ECB8',
  '#E3FCEF': '#ECFDF3',
  '#F3FCF7': '#F6FEF9',
  // Purple
  '#6554C0': '#5E548E',
  '#5243AA': '#4E4680',
  '#998DD9': '#8F86B8',
  '#EAE6FF': '#EEEBF5',
};

// Retints a rendered HTML document to the requested theme. AI renderer
// output is Atlassian-native, so 'atlassian' is a no-op.
export function themeAiHtml(html, themeId) {
  if (!html || themeId !== 'servicenow') return html;
  return String(html).replace(/#[0-9a-fA-F]{6}\b/g, (hex) => ADS_TO_SERVICENOW[hex.toUpperCase()] || hex);
}

// Convenience: theme HTML to the app-default theme (for on-screen display).
export function themeAiHtmlForApp(html) {
  return themeAiHtml(html, appDefaultReportTheme());
}
